import { PUBLIC_LEGAL_LINKS, SORTEIOS_SITE_ORIGIN } from '@/lib/public-legal-links'

export interface SorteeLandingSeoContent {
  browserTitle: string
  description: string
  keywords: string
  author: string
  robots: string
  canonical: string
  themeColor: string
  og: {
    type: 'website'
    url: string
    title: string
    description: string
    image: string
    imageWidth: string
    imageHeight: string
    locale: string
    siteName: string
  }
  twitter: {
    card: 'summary_large_image'
    site: string
    title: string
    description: string
    image: string
  }
  jsonLd: Record<string, unknown>
}

export const sorteeLandingNavLinks = [
  { label: 'Funcionalidades', href: '#features' },
  { label: 'Como funciona', href: '#how' },
  { label: 'FAQ', href: '#faq' },
]

export const sorteeLandingBrand = {
  name: 'sortee',
  homeAriaLabel: 'Sortee, página inicial',
  navAriaLabel: 'Navegação principal',
  menuAriaLabel: 'Abrir menu',
  proofAriaLabel: 'Prova social',
  logoHref: '/',
}

export const sorteeLandingNavActions = {
  loginLabel: 'Entrar',
  loginHref: '/login',
  signupLabel: 'Começar grátis',
  signupHref: '/cadastro',
}

export const sorteeLandingHero = {
  headlineLineOne: 'Crie campanhas',
  headlineLineTwo: 'de',
  headlineCycleWords: ['sorteios', 'gamificação', 'resultados', 'sorteios'],
  headlineLineThree: 'que convertem',
  descriptionPrefix: 'Do cadastro ao sorteio. ',
  descriptionHighlight: 'Roleta, afiliados, CAPI Meta',
  descriptionSuffix:
    ' e analytics em tempo real. Tudo em um único lugar para campanhas que geram receita de verdade.',
  primaryActionLabel: 'Criar campanha grátis',
  primaryActionHref: '/cadastro',
  secondaryActionLabel: 'Ver demonstração',
  secondaryActionHref: '#how',
  trustLabel: '+2.400 campanhas criadas esta semana',
  trustStrongLabel: '+2.400 campanhas',
  trustSuffixLabel: ' criadas esta semana',
  trustStars: '★★★★★',
}

export const sorteeLandingHeroDashboard = {
  ariaLabel: 'Dashboard do Sortee mostrando métricas de uma campanha ativa',
  urlPreview: 'sortee.io/dashboard',
  title: 'Dashboard',
  subtitle: 'Missão Onça Pintada · sortee.io/oncapintada',
  liveBadge: 'AO VIVO',
  revenueLegend: 'Receita',
  salesLegend: 'Vendas',
  growthLabel: '↑ 34% este mês',
}

export const sorteeLandingTrustAvatars = [
  { initials: 'MC', background: 'linear-gradient(135deg,#7c3aed,#d946ef)' },
  { initials: 'RP', background: 'linear-gradient(135deg,#06b6d4,#7c3aed)' },
  { initials: 'FA', background: 'linear-gradient(135deg,#d946ef,#ec4899)' },
  { initials: 'JS', background: 'linear-gradient(135deg,#10b981,#06b6d4)' },
  { initials: '+', background: 'linear-gradient(135deg,#f59e0b,#ef4444)' },
]

export const sorteeLandingFloatingPills = [
  {
    className: 'fp1',
    icon: '✅',
    iconBackground: 'rgba(5,150,105,.1)',
    title: 'Pagamento confirmado',
    titleColor: '#059669',
    subtitle: 'R$ 50,00 · Missão Onça Pintada',
  },
  {
    className: 'fp2',
    icon: '🏆',
    iconBackground: 'rgba(217,119,6,.1)',
    title: 'Ganhador sorteado!',
    titleColor: '#d97706',
    subtitle: 'Nº 847.231 · Campanha Verão',
  },
  {
    className: 'fp3',
    icon: '🎡',
    iconBackground: 'rgba(124,58,237,.1)',
    title: '+2 números bônus',
    titleColor: '#7c3aed',
    subtitle: 'Roleta da sorte ativada',
  },
]

export const sorteeLandingDashboardStats = [
  { icon: '🎟️', label: 'Nºs vendidos', value: '1.247', color: '#7c3aed' },
  { icon: '💳', label: 'Pagamentos', value: 'R$624', color: '#059669' },
  { icon: '👥', label: 'Compradores', value: '312', color: '#d97706' },
  { icon: '📈', label: 'Ticket médio', value: 'R$18', color: '#db2777' },
]

export const sorteeLandingDashboardBars = [
  { height: '18%', background: 'rgba(124,58,237,.2)' },
  { height: '28%', background: 'rgba(124,58,237,.28)' },
  { height: '20%', background: 'rgba(124,58,237,.22)' },
  { height: '52%', background: 'rgba(139,92,246,.5)' },
  { height: '40%', background: 'rgba(139,92,246,.44)' },
  { height: '72%', background: 'rgba(217,70,239,.6)' },
  { height: '62%', background: 'rgba(217,70,239,.55)' },
  { height: '90%', background: 'linear-gradient(to top,#7c3aed,#d946ef)' },
  { height: '80%', background: 'linear-gradient(to top,#7c3aed,#d946ef)' },
  { height: '100%', background: 'linear-gradient(to top,#d946ef,#db2777)' },
]

export const sorteeLandingProofItems = [
  {
    target: '4',
    prefix: 'R$ ',
    suffix: 'M+',
    label: 'Arrecadados pelas campanhas',
    subLabel: '↑ 120% vs. ano anterior',
  },
  {
    target: '2400',
    suffix: '+',
    label: 'Campanhas criadas',
    subLabel: 'Em 18 estados brasileiros',
  },
  {
    target: '180',
    suffix: 'k',
    label: 'Participantes ativos',
    subLabel: '↑ 89% de retenção',
  },
  {
    target: '98',
    suffix: '%',
    label: 'Taxa de satisfação',
    subLabel: 'NPS +72 em 2026',
  },
]

export const sorteeLandingMarqueeItems = [
  'Sorteios automatizados',
  'Roleta da sorte',
  'Gateway integrado',
  'Afiliados com comissão automática',
  'Meta Conversions API',
  'Dashboard em tempo real',
  'Ranking de ganhadores',
  'Google Analytics integrado',
  'PIX · Cartão · Boleto',
]

export const sorteeLandingFeaturesHeader = {
  tag: 'Funcionalidades',
  headingLineOne: 'Tudo que sua campanha',
  headingLineTwo: 'precisa para converter',
  sideText: 'Do cadastro ao sorteio, tudo integrado. Sem plugins, sem gambiarras.',
}

export const sorteeLandingFeaturesVisualContent = {
  dashboardIcon: '📊',
  rouletteIcon: '🎡',
  gatewayIcon: '💳',
  capiIcon: '📡',
  drawIcon: '🏆',
  affiliatesIcon: '🤝',
  campaignsIcon: '🎯',
  wheelCenterLabel: 'GIRAR',
  wheelTexts: ['50% OFF', '+2 Nºs', '6% desc', '+3 Nºs', '20% OFF', '15% desc'],
}

export const sorteeLandingFeatures = {
  dashboard: {
    title: 'Dashboard em tempo real',
    description:
      'Acompanhe vendas, pagamentos, receita e compradores ao vivo. Gráfico de performance temporal com melhor dia, pico de pagamentos e taxa de conversão. Tudo num único painel.',
    miniStats: [
      { value: 'R$41K', color: '#7c3aed', label: 'receita total' },
      { value: '94%', color: '#059669', label: 'tx. pagamento' },
      { value: '13/04', color: '#d97706', label: 'melhor dia' },
      { value: '7.4%', color: '#db2777', label: 'conversão' },
    ],
  },
  roulette: {
    title: 'Roleta da Sorte',
    description:
      'Gamifique a compra com prêmios instantâneos. Cupons, números bônus e descontos que criam urgência e aumentam o ticket médio.',
  },
  gateway: {
    title: 'Gateway integrado',
    description: 'PIX, cartão e boleto. Confirmação automática, zero configuração extra.',
    tags: [
      {
        label: 'PIX instantâneo',
        style:
          'background:rgba(5,150,105,.1);border:1px solid rgba(5,150,105,.2);color:#059669;',
      },
      {
        label: 'Cartão de crédito',
        style:
          'background:rgba(124,58,237,.1);border:1px solid rgba(124,58,237,.2);color:#7c3aed;',
      },
      {
        label: 'Boleto bancário',
        style:
          'background:rgba(217,119,6,.1);border:1px solid rgba(217,119,6,.2);color:#d97706;',
      },
    ],
  },
  capi: {
    title: 'Meta Conversions API',
    description:
      'Rastreamento server-side sem cookies. Seus anúncios otimizados com dados reais de compra.',
    funnel: [
      {
        label: 'Visualizações',
        value: '162',
        width: '100%',
        rowStyle: 'background:rgba(124,58,237,.1);',
        valueColor: '#7c3aed',
      },
      {
        label: 'Checkout',
        value: '26',
        width: '85%',
        rowStyle: 'background:rgba(8,145,178,.1);',
        valueColor: '#0891b2',
      },
      {
        label: 'Compras ✓',
        value: '12',
        width: '55%',
        rowStyle: 'background:rgba(5,150,105,.12);',
        valueColor: '#059669',
      },
    ],
  },
  draw: {
    title: 'Sorteio automático',
    description:
      'Transparente, auditável e justo. Ganhadores publicados em segundos na página pública com total credibilidade.',
  },
  affiliates: {
    title: 'Programa de afiliados',
    description:
      'Links únicos, comissão automática. Seus clientes se tornam vendedores e impulsionam o crescimento viral da sua campanha.',
    list: [
      {
        initials: 'MR',
        name: 'Maria R.',
        value: 'R$ 284,00',
        avatarBackground: 'linear-gradient(135deg,#7c3aed,#d946ef)',
      },
      {
        initials: 'JP',
        name: 'João P.',
        value: 'R$ 176,00',
        avatarBackground: 'linear-gradient(135deg,#06b6d4,#7c3aed)',
      },
      {
        initials: 'AS',
        name: 'Ana S.',
        value: 'R$ 143,00',
        avatarBackground: 'linear-gradient(135deg,#d946ef,#db2777)',
      },
    ],
  },
  campaigns: {
    title: 'Campanhas personalizadas',
    description:
      'Slug próprio, imagem de capa, regras, prêmios e página pública. Configure em minutos, publique em um clique. Multi-campanha, multi-instituição, tudo centralizado no mesmo painel.',
    counters: [
      {
        value: '3',
        label: 'Ativas',
        style: 'background:rgba(5,150,105,.08);border:1px solid rgba(5,150,105,.18);',
        valueColor: '#059669',
      },
      {
        value: '1M',
        label: 'Nºs disponíveis',
        style: 'background:rgba(217,119,6,.08);border:1px solid rgba(217,119,6,.18);',
        valueColor: '#d97706',
      },
      {
        value: '∞',
        label: 'Afiliados',
        style: 'background:rgba(219,39,119,.08);border:1px solid rgba(219,39,119,.18);',
        valueColor: '#db2777',
      },
    ],
  },
}

export const sorteeLandingHow = {
  tag: 'Como funciona',
  headingLineOne: 'De zero a campanha',
  headingLineTwo: '3 passos simples',
  steps: [
    {
      title: 'Crie a campanha',
      description:
        'Nome, slug, prêmios e quantidade de números. A página pública fica pronta em minutos, com URL própria e design profissional.',
      cardTitle: 'Criar campanha',
      cardType: 'create',
    },
    {
      title: 'Divulgue e venda',
      description:
        'Compartilhe o link, ative afiliados e aceite PIX, cartão ou boleto. Tudo confirmado e registrado automaticamente no dashboard.',
      cardTitle: 'Vendas em tempo real',
      cardType: 'sales',
    },
    {
      title: 'Sorteie e celebre',
      description:
        'Execute o sorteio automático ao final da campanha. Ganhadores são publicados na página pública com total transparência.',
      cardTitle: 'Sorteio realizado!',
      cardType: 'draw',
    },
  ],
}

export const sorteeLandingHowCards = {
  create: {
    campaignNameLabel: 'Nome da campanha',
    campaignNameValue: 'Missão Onça Pintada',
    slugLabel: 'Slug (URL pública)',
    slugValue: 'oncapintada',
    totalNumbersLabel: 'Total de números',
    totalNumbersValue: '1.000.000',
    numberPriceLabel: 'Preço por número',
    numberPriceValue: 'R$ 0,50',
    actionLabel: 'Criar campanha →',
    actionHref: '/cadastro',
  },
  sales: {
    entries: [
      { label: 'Erick B. · PIX', value: 'R$ 10,00 ✓' },
      { label: 'Fabio T. · Cartão', value: 'R$ 25,00 ✓' },
      { label: 'Maria C. · PIX', value: 'R$ 50,00 ✓' },
    ],
    totalLabel: 'Receita do dia',
    totalValue: 'R$ 624,00',
  },
  draw: {
    emoji: '🏆',
    winnerNumber: 'Nº 847.231',
    winnerNumberLabel: 'Número sorteado com sucesso',
    winnerInitials: 'MR',
    winnerName: 'Maria Rodrigues',
    winnerContact: 'maria@email.com · (51) 9 9999-0000',
    winnerBadge: 'GANHADORA',
    actionLabel: 'Publicar resultado →',
  },
}

export const sorteeLandingTestimonialsHeading = {
  tag: 'Depoimentos',
  lead: 'Quem usa, ',
  highlight: 'não volta atrás',
}

export const sorteeLandingTestimonials = [
  {
    stars: '★★★★★',
    quote:
      '"Em 30 dias arrecadamos R$ 28.000. A roleta da sorte foi o diferencial. Os participantes ficavam animados para girar e acabavam comprando mais números."',
    initials: 'MC',
    name: 'Maria Clara',
    role: 'Diretora · Instituto Esperança',
    avatarBackground: 'linear-gradient(135deg,#7c3aed,#d946ef)',
  },
  {
    stars: '★★★★★',
    quote:
      '"O programa de afiliados triplicou nossas vendas sem gastar mais em anúncios. Em 2 semanas, 40 afiliados ativos indicando para as redes deles."',
    initials: 'RP',
    name: 'Ricardo Pinto',
    role: 'CEO · Santaplay',
    avatarBackground: 'linear-gradient(135deg,#d946ef,#db2777)',
  },
  {
    stars: '★★★★★',
    quote:
      '"O dashboard em tempo real mudou nossa estratégia. Sabemos exatamente quando os picos acontecem e conseguimos impulsionar nos momentos certos."',
    initials: 'FA',
    name: 'Fernanda Alves',
    role: 'Head de Marketing · Verão Premiado',
    avatarBackground: 'linear-gradient(135deg,#06b6d4,#7c3aed)',
  },
]

export const sorteeLandingFaq = {
  tag: 'FAQ',
  headingLead: 'Perguntas ',
  headingHighlight: 'frequentes',
  description: 'Tudo que você precisa saber antes de começar.',
  items: [
    {
      question: 'O Sortee é gratuito?',
      answer:
        'Sim! O plano Starter é gratuito, sem cartão de crédito. 1 campanha ativa com até 1.000 números e sorteio automático sem custo algum.',
    },
    {
      question: 'Quais formas de pagamento são aceitas?',
      answer:
        'PIX instantâneo, cartão de crédito e boleto bancário. Todos confirmados e registrados automaticamente no dashboard, sem configuração adicional.',
    },
    {
      question: 'O que é a Roleta da Sorte?',
      answer:
        'Recurso de gamificação onde o comprador gira uma roleta após a compra e ganha prêmios instantâneos (cupons, números bônus, descontos). Aumenta o engajamento e o ticket médio da campanha.',
    },
    {
      question: 'O que é a Meta Conversions API (CAPI)?',
      answer:
        'Integração nativa server-side com o Meta Ads para rastreamento de eventos de compra sem cookies de terceiros. Melhora a qualidade dos dados e o ROAS dos seus anúncios.',
    },
    {
      question: 'Como funciona o programa de afiliados?',
      answer:
        'Links únicos rastreáveis para cada afiliado, com comissões pagas automaticamente. Seus clientes se tornam vendedores, gerando crescimento viral orgânico sem custo adicional.',
    },
    {
      question: 'O sorteio é transparente?',
      answer:
        'Sim. O sorteio é automático, auditável e publica o resultado na página pública da campanha em segundos, com total credibilidade para os participantes.',
    },
  ],
}

export const sorteeLandingCta = {
  tag: 'Comece hoje',
  headingLineOne: 'Sua próxima campanha',
  headingLineTwo: 'agora mesmo',
  description:
    'Junte-se a mais de 2.400 campanhas que já usam o Sortee para engajar participantes e gerar resultados reais.',
  primaryActionLabel: 'Criar campanha grátis',
  primaryActionHref: '/cadastro',
  secondaryActionLabel: 'Agendar demonstração →',
  secondaryActionHref: '/demo',
}

export interface SorteeLandingFooterLink {
  label: string
  href: string
  openInNewTab?: boolean
}

export const sorteeLandingFooter = {
  contactHeading: 'Fale com nosso time!',
  contactDescription:
    'Legalize sorteios, crie campanhas promocionais com segurança e evite multas por ações irregulares, com quem atua há quase 30 anos no mercado.',
  phone: {
    label: '+55 48 98428-1160',
    href: 'https://wa.me/5548984281160',
    openInNewTab: true,
  },
  email: {
    label: 'E-mail: realize@sorteios.com.br',
    href: 'mailto:realize@sorteios.com.br',
  },
  socialHeading: 'Redes Sociais',
  socialLinks: [
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/drfabioturnes/',
      openInNewTab: true,
    },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/sorteios.com.br',
      openInNewTab: true,
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/sorteios',
      openInNewTab: true,
    },
    {
      label: 'Youtube',
      href: 'https://www.youtube.com/@sorteios.com.br_oficial',
      openInNewTab: true,
    },
  ] satisfies SorteeLandingFooterLink[],
  groupsPrefix: 'Participe dos Grupos',
  groupsConjunction: 'e',
  groups: [
    {
      label: 'WhatsApp',
      href: 'https://chat.whatsapp.com/Eehk1pi6m1q8b14JO8TYFC',
      openInNewTab: true,
    },
    {
      label: 'Telegram',
      href: 'https://t.me/sorteios_com_br',
      openInNewTab: true,
    },
  ] satisfies SorteeLandingFooterLink[],
  navHeading: 'Links',
  navLinks: [
    { label: 'Home', href: `${SORTEIOS_SITE_ORIGIN}/` },
    { label: 'Sobre nós', href: `${SORTEIOS_SITE_ORIGIN}/backup/` },
    { label: 'Vantagens', href: `${SORTEIOS_SITE_ORIGIN}/#vantagens` },
    { label: 'Soluções', href: `${SORTEIOS_SITE_ORIGIN}/#solucoes` },
    { label: 'Preços', href: `${SORTEIOS_SITE_ORIGIN}/#contato` },
    { label: 'Serviços', href: `${SORTEIOS_SITE_ORIGIN}/servicos/#topo` },
  ] satisfies SorteeLandingFooterLink[],
  solutionsHeading: 'Soluções',
  solutionsLinks: [
    { label: 'Plataforma Sortee', href: 'https://sortee.io/', openInNewTab: true },
    {
      label: 'Concurso Cultural',
      href: `${SORTEIOS_SITE_ORIGIN}/concurso-cultural/#topo`,
    },
    { label: 'Sorteio Filantrópico', href: `${SORTEIOS_SITE_ORIGIN}/#solucoes` },
    { label: 'Sorteio Loteria Federal', href: `${SORTEIOS_SITE_ORIGIN}/#solucoes` },
    { label: 'Sorteio Redes Sociais', href: `${SORTEIOS_SITE_ORIGIN}/#solucoes` },
    {
      label: 'Roleta Sorteio Redes Sociais',
      href: `${SORTEIOS_SITE_ORIGIN}/roleta-da-sorte/`,
    },
  ] satisfies SorteeLandingFooterLink[],
  copyright: 'Copyright ©1997 a 2026 Legal Prime Brasil – Cnpj 01.636.095/0001-05',
  rightsReserved: 'All rights reserved',
  legalLinks: [
    { label: 'Termos de Uso', href: PUBLIC_LEGAL_LINKS.terms },
    {
      label: 'Política de Privacidade',
      href: PUBLIC_LEGAL_LINKS.privacy,
    },
    {
      label: 'Política de Cookies',
      href: `${SORTEIOS_SITE_ORIGIN}/politica-de-cookies/`,
    },
    { label: 'Exclusão de Dados', href: `${SORTEIOS_SITE_ORIGIN}/exclusao-de-dados/` },
  ] satisfies SorteeLandingFooterLink[],
}

export const sorteeLandingSeo: SorteeLandingSeoContent = {
  browserTitle:
    'Sortee: Plataforma de Sorteios e Marketing Promocional | Roleta, Afiliados e CAPI Meta',
  description:
    'Crie campanhas de sorteios com gamificação, roleta da sorte, programa de afiliados e integração Meta CAPI. Dashboard em tempo real, gateway PIX/Cartão/Boleto. Comece grátis no Sortee.',
  keywords:
    'sorteio online, plataforma de sorteios, marketing promocional, roleta da sorte, programa de afiliados, CAPI Meta, gamificação, sorteio automático, dashboard sorteio, sortee',
  author: 'Sortee',
  robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  canonical: 'https://sortee.io/',
  themeColor: '#4c24cb',
  og: {
    type: 'website',
    url: 'https://sortee.io/',
    title: 'Sortee: Plataforma de Sorteios e Marketing Promocional',
    description:
      'Crie campanhas de sorteios com roleta, afiliados e CAPI Meta. Dashboard em tempo real e gateway integrado. Mais de 2.400 campanhas e R$ 4M+ arrecadados.',
    image: 'https://sortee.io/og-image.png',
    imageWidth: '1200',
    imageHeight: '630',
    locale: 'pt_BR',
    siteName: 'Sortee',
  },
  twitter: {
    card: 'summary_large_image',
    site: '@sorteeio',
    title: 'Sortee: Plataforma de Sorteios e Marketing Promocional',
    description:
      'Sorteios com roleta, afiliados e CAPI Meta. +2.400 campanhas criadas, R$ 4M+ arrecadados.',
    image: 'https://sortee.io/og-image.png',
  },
  jsonLd: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://sortee.io/#organization',
        name: 'Sortee',
        url: 'https://sortee.io',
        logo: { '@type': 'ImageObject', url: 'https://sortee.io/logo/sortee.png' },
        description:
          'Plataforma brasileira de sorteios e marketing promocional com gamificação, roleta da sorte, programa de afiliados e integração com Meta Conversions API.',
        addressCountry: 'BR',
      },
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://sortee.io/#software',
        name: 'Sortee',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://sortee.io',
      },
      {
        '@type': 'FAQPage',
        '@id': 'https://sortee.io/#faq',
        mainEntity: sorteeLandingFaq.items.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
    ],
  },
}
